'use client';

/**
 * PaymentProcessingScreen Component
 *
 * Displays payment processing status: spinner, success/error indicators,
 * message, and action buttons.
 * The processor (ChargePaymentComponent) is mounted in PurchaseFlow as a sibling.
 *
 * Success screen displays:
 * - Success icon and okMessage from API
 * - Cashback info ("You earned $X.XX cashback") when cashbackAmount is present
 * - Statement note (parsed HTML) for PayPal or Credit Card
 * - OK button to dismiss
 */

import React, { useState, useCallback } from 'react';
import parse from 'html-react-parser';
import { usePurchaseRender } from '../config/PurchaseFlowConfigProvider';
import Spiner from '../../../../common/helpers/spiner/spiner';
import type { ChargeProcessingState } from '../types';
import { PAYMENT_ERROR_TYPES, PURCHASE_FLOW_TEXT_KEYS, PURCHASE_FLOW_ICONS } from '../utils/constants';
import classes from './classes.module.scss';

// ============================================================================
// Types
// ============================================================================

export interface PaymentProcessingScreenProps {
  chargeState: ChargeProcessingState;
  /** Callback to re-focus the external processing window (PayPal popup).
   *  When provided, the processing message switches to "processing in another window" text. */
  onFocusProcessingWindow?: () => void;
  /** HTML string for statement note (resolved by parent based on payment method) */
  statementNote?: string;
  /** Success button label key (from PURCHASE_FLOW_TEXT_KEYS). Resolved by wrapper via getSuccessButtonTextKey(). */
  successButtonTextKey?: string;
  /** When true, renders the error icon inside the content (for inline/mobile mode where
   *  there is no Popover overlay). Desktop modal mode uses the Popover overlay instead. */
  showInlineErrorIcon?: boolean;
  onOkClick: () => void;
  onRetryClick: () => void;
  onCloseClick: () => void;
  onSpendingLimitsClick?: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

const getErrorButtonTextKey = (error: ChargeProcessingState['error'], onSpendingLimitsClick?: () => void): string => {
  if (error?.errorType === PAYMENT_ERROR_TYPES.SPENDING_LIMITS && onSpendingLimitsClick) return PURCHASE_FLOW_TEXT_KEYS.BUTTON_INCREASE_SPENDING_LIMIT;
  if (error?.recoverable || error?.retriable) return PURCHASE_FLOW_TEXT_KEYS.BUTTON_RETRY;
  return PURCHASE_FLOW_TEXT_KEYS.BUTTON_OK;
};

const isRetriableError = (error: ChargeProcessingState['error']): boolean => !!(error?.recoverable || error?.retriable);

/**
 * Formats cashback amount as "$X.XX"
 */
const formatCashbackAmount = (cashbackAmount?: string): string | null => {
  if (!cashbackAmount) return null;
  const amount = parseFloat(cashbackAmount);
  if (Number.isNaN(amount) || amount <= 0) return null;
  return `$${ amount.toFixed(2) }`;
};

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Renders cashback info line: "{earned text} $X.XX {cashback label}"
 */
const CashbackInfo: React.FC<{ cashbackAmount?: string }> = ({ cashbackAmount }) => {
  const { renderText } = usePurchaseRender();
  const formatted = formatCashbackAmount(cashbackAmount);
  if (!formatted) return null;

  return (
    <span className={ classes.smallInfo }>
      {renderText(PURCHASE_FLOW_TEXT_KEYS.CASHBACK_EARNED)}
      {' '}
      <b>{formatted}</b>
      {' '}
      {renderText(PURCHASE_FLOW_TEXT_KEYS.CASHBACK_LABEL)}
    </span>
  );
};

/**
 * Renders statement note (parsed HTML from config)
 */
const StatementNote: React.FC<{ note?: string }> = ({ note }) => {
  if (!note) return null;

  return (
    <span className={ classes.smallInfo }>
      {parse(note)}
    </span>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const PaymentProcessingScreen: React.FC<PaymentProcessingScreenProps> = ({
  chargeState,
  onFocusProcessingWindow,
  statementNote,
  successButtonTextKey: successButtonTextKeyProp,
  showInlineErrorIcon = false,
  onOkClick,
  onRetryClick,
  onCloseClick,
  onSpendingLimitsClick
}) => {
  const { renderIcon, renderText } = usePurchaseRender();
  const successButtonTextKey = successButtonTextKeyProp ?? PURCHASE_FLOW_TEXT_KEYS.BUTTON_OK;
  const {
    status, result, error
  } = chargeState;
  const isSpendingLimitsError = error?.errorType === PAYMENT_ERROR_TYPES.SPENDING_LIMITS;
  const isPopupBlockedError = error?.errorType === PAYMENT_ERROR_TYPES.POPUP_BLOCKED;
  const hasSuccessInfo = !!(result?.cashbackAmount || statementNote);

  // Guard against double-clicks on action buttons (OK, Retry, Close, Spending Limits)
  const [actionClicked, setActionClicked] = useState(false);

  const guardedClick = useCallback((handler: () => void) => {
    if (actionClicked) return;
    setActionClicked(true);
    handler();
  }, [actionClicked]);

  const handleOkButtonClick = useCallback(() => guardedClick(onOkClick), [guardedClick, onOkClick]);

  const handleErrorButtonClick = useCallback(() => {
    guardedClick(() => {
      if (isSpendingLimitsError && onSpendingLimitsClick) onSpendingLimitsClick();
      else if (isRetriableError(error)) onRetryClick?.();
      else onCloseClick?.();
    });
  }, [guardedClick, isSpendingLimitsError, error, onSpendingLimitsClick, onRetryClick, onCloseClick]);

  return (
    <div className={ classes.statusContainer } data-testid="payment-processing-container">
      {status === 'success' && (
        <>
          <div className={ classes.successIcon } data-testid="payment-processing-success-icon">{renderIcon(PURCHASE_FLOW_ICONS.SUCCESS)}</div>
          {result?.okMessage && <div className={ classes.processingMessage } data-testid="payment-processing-success-message">{result.okMessage}</div>}
          <button type="button" onClick={ handleOkButtonClick } className={ classes.processingActionButton } disabled={ actionClicked } data-testid="payment-processing-ok-button">
            {renderText(successButtonTextKey)}
          </button>
          {hasSuccessInfo && (
            <div className={ classes.successInfoContainer } data-testid="payment-processing-success-info">
              <CashbackInfo cashbackAmount={ result?.cashbackAmount } />
              <StatementNote note={ statementNote } />
            </div>
          )}
        </>
      )}

      {status === 'error' && (
        <>
          {showInlineErrorIcon && (
            <div className={ classes.inlineErrorIcon }>
              {renderIcon(PURCHASE_FLOW_ICONS.PROCESSING_ERROR_MOBILE)}
            </div>
          )}
          <div className={ classes.errorTitle } data-testid="payment-processing-error-title">
            {isPopupBlockedError
              ? renderText(PURCHASE_FLOW_TEXT_KEYS.ERROR_POPUP_BLOCKED_TITLE)
              : (error?.errorTitle || renderText(PURCHASE_FLOW_TEXT_KEYS.ERROR_TITLE))}
          </div>
          {isPopupBlockedError
            ? <div className={ classes.processingMessage } data-testid="payment-processing-error-message">{parse(renderText(PURCHASE_FLOW_TEXT_KEYS.ERROR_POPUP_BLOCKED_MESSAGE))}</div>
            : (error?.errorMessage && <div className={ classes.processingMessage } data-testid="payment-processing-error-message">{error.errorMessage}</div>)}
          <button
            type="button"
            onClick={ handleErrorButtonClick }
            className={ classes.processingActionButton }
            disabled={ actionClicked }
            data-testid="payment-processing-error-action-button"
          >
            {renderText(getErrorButtonTextKey(error, onSpendingLimitsClick))}
          </button>
        </>
      )}

      {status !== 'success' && status !== 'error' && (
        <>
          <div className={ classes.processingMessage } data-testid="payment-processing-message">
            {onFocusProcessingWindow
              ? renderText(PURCHASE_FLOW_TEXT_KEYS.PROCESSING_EXTERNAL_WINDOW)
              : renderText(PURCHASE_FLOW_TEXT_KEYS.TITLE_PROCESSING)}
            {onFocusProcessingWindow && (
              <>
                {' '}
                <button type="button" className={ classes.redirectButton } onClick={ onFocusProcessingWindow } data-testid="payment-processing-focus-window-button">
                  {renderText(PURCHASE_FLOW_TEXT_KEYS.PROCESSING_REDIRECT_LINK)}
                </button>
              </>
            )}
          </div>
          <Spiner className={ classes.processingSpiner } loaderColor="var(--app-default-btn-color)" />
        </>
      )}
    </div>
  );
};

export default PaymentProcessingScreen;
